import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Button,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import AddCompanyDepartment from "./partials/AddCompanyDepartments/AddCompanyDepartments";
import DeleteCompanyDepartment from "./partials/DeleteCompanyDepartments/DeleteCompanyDepartments";
import {
  ICompanyDepartments,
  ICompanyDepartmentsInput,
  ICompanyDepartmentsInputEdit,
} from "models/companyDepartment";
import {
  CompanyDepartmentsCells,
  addCompanyDepartment,
  deleteCompanyDepartment,
  editCompanyDepartment,
  getCompanyDepartments,
} from "framework/companyDepartments";
import { useTranslation } from "react-i18next";
import { IDepartments } from "models/departments";
import { getDepartments } from "framework/departments";
import { IPermission } from "models/permissions";
import BlockIcon from "@mui/icons-material/Block";
import { getPermission, getUserData } from "helpers";
import EditCompanyDepartment from "./partials/EditCompanyDepartments/EditCompanyDepartments";
import useFetchSection from "hooks/useFetchSection";
import { UserRoleName } from "types/auth";
import { importCompetency, importDepartments } from "framework/competencies";
import { getCompanyEmployees } from "framework/company";
import { ICompanyEmployees } from "models/company";
interface Props {
  feature: Feature;
}

const CompanyDepartments: React.FC<Props> = ({ feature }) => {
  const [loading2, setLoading2] = useState<boolean>(false);
  const [loading3, setLoading3] = useState<boolean>(true);

  const [FilterCompanyDepartments, setFilterCompanyDepartments] = useState<
    ICompanyDepartments[]
  >([] as ICompanyDepartments[]);
  const { t, i18n } = useTranslation();
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedCompanyDepartment, setSelectedCompanyDepartment] =
    useState<ICompanyDepartments>({} as ICompanyDepartments);
  const [inputObj, setInputObj] = useState<ICompanyDepartmentsInputEdit>(
    {} as ICompanyDepartmentsInputEdit
  );
  const [Departments, setDepartments] = useState<IDepartments[]>(
    [] as IDepartments[]
  );
  const [CompanyEmployees, setCompanyEmployees] = useState<ICompanyEmployees[]>(
    [] as ICompanyEmployees[]
  );
  const { setAlert } = useAlert();
  const user = getUserData();
  const { colors, roleName } = user;
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addDepartmentsMutation = useMutation({
    mutationFn: (createInput: ICompanyDepartmentsInput) => {
      return addCompanyDepartment(createInput);
    },
  });

  const editDepartmentsMutation = useMutation({
    mutationFn: (createInput: ICompanyDepartmentsInputEdit) => {
      return editCompanyDepartment(createInput, selectedCompanyDepartment.id);
    },
  });

  const deleteDepartmentsMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteCompanyDepartment(id);
    },
  });

  const importDepartmentsMutation = useMutation({
    mutationFn: () => {
      return importDepartments();
    },
  });

  const fetchDepartments = useCallback(async () => {
    setLoading3(true);
    try {
      let response: any = await queryClient.fetchQuery("get Departments", () =>
        getDepartments("")
      );

      setDepartments(response);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);


  const fetchCompanyEmployees = useCallback(async () => {
    setLoading3(true);
    try {
      let response: any = await queryClient.fetchQuery("get Company Employees", () =>
        getCompanyEmployees("")
      );

      setCompanyEmployees(response);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);






  useEffect(() => {
    fetchDepartments();
    fetchCompanyEmployees()
    // eslint-disable-next-line
  }, []);


  const {
    loading,
    data: CompanyDepartments,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getCompanyDepartments(),
    fetchPermission: () => getPermission(17),
    section: "Company Departments",
    onSuccess: (data) => setFilterCompanyDepartments(data),
  });

  if (loading || loading3)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);

  const handleOpenEdit = (Country: ICompanyDepartments) => {
    setSelectedCompanyDepartment(Country);
    setOpenEdit(true);
  };

  const handleOpenDelete = (Country: ICompanyDepartments) => {
    setSelectedCompanyDepartment(Country);
    setOpenDelete(true);
  };

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setSelectedCompanyDepartment({} as ICompanyDepartments);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedCompanyDepartment({} as ICompanyDepartments);
    setInputObj({} as ICompanyDepartmentsInputEdit);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedCompanyDepartment({} as ICompanyDepartments);
  };

  const getBodyCells = (row: ICompanyDepartments) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "headOfDepartmentName")
        return {
          id: "headOfDepartmentName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.headOfDepartmentName}</Typography>
            </TableCell>
          ),
        };

      if (key === "headOfDepartmentNameAr")
        return {
          id: "headOfDepartmentNameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.headOfDepartmentNameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "approvingOfficerName")
        return {
          id: "approvingOfficerName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.approvingOfficerName}</Typography>
            </TableCell>
          ),
        };

      if (key === "approvingOfficerNameAr")
        return {
          id: "approvingOfficerNameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.approvingOfficerNameAr}</Typography>
            </TableCell>
          ),
        };

      // if (key === "customTitle")
      //   return {
      //     id: "customTitle",
      //     element: (
      //       <TableCell align={"left"} key={i}>
      //         <Typography variant="body2">{row?.customTitle}</Typography>
      //       </TableCell>
      //     ),
      //   };

      // if (key === "customTitleAr")
      //   return {
      //     id: "customTitleAr",
      //     element: (
      //       <TableCell align={"left"} key={i}>
      //         <Typography variant="body2">{row?.customTitleAr}</Typography>
      //       </TableCell>
      //     ),
      //   };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async (data: any[]) => {
    if (!!!data.length)
      return setAlert({
        open: true,
        message: "Please choose a department",
        type: "error",
      });
    try {
      setLoading2(true);

      await addDepartmentsMutation.mutateAsync({ departments: data });
      setAlert({
        open: true,
        message: "Company Department added successfully",
        type: "success",
      });
      revalidate();
      fetchDepartments();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    // if (
    //   CompanyDepartments.find(
    //     (d) => d.departmentId === inputObj.departmentId
    //   ) &&
    //   inputObj.departmentId !== selectedCompanyDepartment.departmentId
    // ) {
    //   setAlert({
    //     open: true,
    //     message: "Department Already Exists",
    //     type: "info",
    //   });
    //   return;
    // }
    try {
      setLoading2(true);
      await editDepartmentsMutation.mutateAsync({
        id: selectedCompanyDepartment.id,
        name: inputObj.name || selectedCompanyDepartment.name,
        nameAr: inputObj.nameAr || selectedCompanyDepartment.nameAr,
        headOfDepartment: inputObj.headOfDepartment || selectedCompanyDepartment.headOfDepartment,
        approvingOfficer: inputObj.approvingOfficer || selectedCompanyDepartment.approvingOfficer,
      });
      setAlert({
        open: true,
        message: "Company Department Edit successfully",
        type: "success",
      });
      revalidate();
      fetchDepartments();
      handleCloseEdit();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteDepartmentsMutation.mutateAsync(selectedCompanyDepartment.id);
      setAlert({
        open: true,
        message: "Company Department deleted successfully",
        type: "success",
      });
      revalidate();
      fetchDepartments();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleImport = async () => {
    try {
      setLoading2(true);

      await importDepartmentsMutation.mutateAsync();
      setAlert({
        open: true,
        message: "Department successfully imported from the master list ",
        type: "success",
      });
      revalidate();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterCompanyDepartments(CompanyDepartments!);
    let filtered = CompanyDepartments!.filter((item) =>
      (item.name + " " + item.nameAr + " " + item.headOfDepartmentName + " " + item.approvingOfficerName + " " + item.headOfDepartmentNameAr + " " + item.approvingOfficerNameAr).toLowerCase().includes(value.toLowerCase())
    );
    setFilterCompanyDepartments(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title="Add CompanyDepartments">
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {roleName === UserRoleName.COMPANY_ADMIN && <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained" onClick={handleImport} >
            {t('T_import_from_master')}
          </Button>
        </div>}

        {permission?.hasReadPermission! &&
          FilterCompanyDepartments.length > 0 && (
            <List
              headCells={CompanyDepartmentsCells}
              data={FilterCompanyDepartments}
              getBodyCells={getBodyCells}
              feature={feature}
              canEdit={false}
            />
          )}
        {(!FilterCompanyDepartments || FilterCompanyDepartments.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_department_header")}
              message={t("T_no_department_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddCompanyDepartment
          CompanyDepartments={CompanyDepartments!}
          open={openAdd}
          loading={loading2}
          companyEmployees={CompanyEmployees}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
        />
      )}
      {openEdit && (
        <EditCompanyDepartment
          open={openEdit}
          companyEmployees={CompanyEmployees}
          loading={loading2}
          CompanyDepartments={selectedCompanyDepartment}
          onChanges={handleChange}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
        />
      )}

      {openDelete && (
        <DeleteCompanyDepartment
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default CompanyDepartments;
